<template>
  <div class="document-classifier">
    <div class="header">
      <h2>Document Classification</h2>
      <div class="confidence-indicator" :class="confidenceClass">
        <span class="confidence-label">Confidence:</span>
        <span class="confidence-value">{{ confidencePercentage }}%</span>
      </div>
    </div>

    <div class="content">
      <div class="document-preview">
        <img v-if="documentImage" :src="documentImage" alt="Document Preview" @load="onImageLoad" />
        <div v-else class="no-image">No document image available</div>
      </div>

      <div class="classification-panel">
        <div class="detected-type">
          <h3>Detected Document Type</h3>
          <div class="type-badge" :class="{ 'is-verified': isVerified }">
            {{ detectedType }}
            <span v-if="isVerified" class="verified-icon">✓</span>
          </div>
          <button 
            v-if="!isVerified" 
            class="verify-button" 
            @click="verifyClassification"
          >
            Verify Classification
          </button>
        </div>

        <div class="alternative-types">
          <h3>Alternative Document Types</h3>
          <div v-if="alternativeTypes.length > 0" class="types-list">
            <div 
              v-for="(type, index) in alternativeTypes" 
              :key="index" 
              class="type-item"
              @click="selectAlternativeType(type)"
            >
              <div class="type-name">{{ type.name }}</div>
              <div class="type-confidence">{{ type.confidence }}%</div>
            </div>
          </div>
          <div v-else class="no-alternatives">
            No alternative classifications available
          </div>
        </div>

        <div class="manual-classification">
          <h3>Manual Classification</h3>
          <div class="form-group">
            <label for="document-type">Document Type:</label>
            <select 
              id="document-type" 
              v-model="manualType"
              @change="updateManualType"
            >
              <option value="">Select a document type</option>
              <option v-for="type in availableDocumentTypes" :key="type.id" :value="type.id">
                {{ type.name }}
              </option>
            </select>
          </div>
          <button 
            class="apply-button" 
            :disabled="!manualType" 
            @click="applyManualClassification"
          >
            Apply Manual Classification
          </button>
        </div>
      </div>
    </div>

    <div class="actions">
      <button class="primary-button" @click="saveClassification">Save Classification</button>
      <button class="secondary-button" @click="cancel">Cancel</button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';

// Props
const props = defineProps({
  documentId: {
    type: String,
    required: true
  },
  documentImage: {
    type: String,
    required: true
  },
  initialClassification: {
    type: Object,
    default: () => ({
      type: null,
      confidence: 0,
      alternatives: []
    })
  }
});

// Emits
const emit = defineEmits(['save', 'cancel']);

// State
const detectedType = ref(props.initialClassification.type || 'Unknown');
const confidence = ref(props.initialClassification.confidence || 0);
const alternativeTypes = ref(props.initialClassification.alternatives || []);
const isVerified = ref(false);
const manualType = ref('');
const imageLoaded = ref(false);

// Available document types
const availableDocumentTypes = ref([
  { id: 'g702', name: 'G702 - Application for Payment' },
  { id: 'g703', name: 'G703 - Continuation Sheet' },
  { id: 'budget', name: 'Developer Budget' },
  { id: 'gc_budget', name: 'General Contractor Budget' },
  { id: 'schedule', name: 'Construction Schedule' },
  { id: 'invoice', name: 'Soft Cost Invoice' },
  { id: 'requisition', name: 'Borrower Requisition Letter' },
  { id: 'contract', name: 'Construction Contract' },
  { id: 'other', name: 'Other Document' }
]);

// Computed properties
const confidencePercentage = computed(() => {
  return Math.round(confidence.value);
});

const confidenceClass = computed(() => {
  if (confidence.value >= 80) return 'high-confidence';
  if (confidence.value >= 50) return 'medium-confidence';
  return 'low-confidence';
});

// Methods
function onImageLoad() {
  imageLoaded.value = true;
}

function verifyClassification() {
  isVerified.value = true;
  // You might want to add additional logic here, such as updating the backend
}

function selectAlternativeType(type) {
  detectedType.value = type.name;
  confidence.value = type.confidence;
  isVerified.value = false;
}

function updateManualType() {
  if (manualType.value) {
    const selectedType = availableDocumentTypes.value.find(type => type.id === manualType.value);
    if (selectedType) {
      detectedType.value = selectedType.name;
      confidence.value = 100; // Manual selection has 100% confidence
      isVerified.value = true;
    }
  }
}

function applyManualClassification() {
  updateManualType();
}

function saveClassification() {
  const selectedTypeId = manualType.value || 
    availableDocumentTypes.value.find(type => type.name === detectedType.value)?.id || 
    'unknown';
  
  emit('save', {
    documentId: props.documentId,
    classification: {
      type: selectedTypeId,
      typeName: detectedType.value,
      confidence: confidence.value,
      isVerified: isVerified.value
    }
  });
}

function cancel() {
  emit('cancel');
}

// Lifecycle hooks
onMounted(() => {
  // Initialize with the first available document type if none is provided
  if (!props.initialClassification.type && availableDocumentTypes.value.length > 0) {
    const firstType = availableDocumentTypes.value[0];
    detectedType.value = firstType.name;
    manualType.value = firstType.id;
  }
});
</script>

<style scoped>
.document-classifier {
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: #f5f7fa;
  border-radius: 8px;
  overflow: hidden;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background-color: #ffffff;
  border-bottom: 1px solid #e1e4e8;
}

.header h2 {
  margin: 0;
  font-size: 20px;
  color: #24292e;
}

.confidence-indicator {
  display: flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 16px;
  font-weight: 500;
}

.confidence-label {
  margin-right: 8px;
}

.high-confidence {
  background-color: #e6f7e6;
  color: #28a745;
}

.medium-confidence {
  background-color: #fff8e6;
  color: #f0ad4e;
}

.low-confidence {
  background-color: #ffeaea;
  color: #dc3545;
}

.content {
  display: flex;
  flex: 1;
  overflow: hidden;
}

.document-preview {
  flex: 1;
  padding: 16px;
  overflow: auto;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  background-color: #e9ecef;
}

.document-preview img {
  max-width: 100%;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.no-image {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  color: #6c757d;
  font-style: italic;
}

.classification-panel {
  width: 320px;
  padding: 16px;
  background-color: #ffffff;
  border-left: 1px solid #e1e4e8;
  overflow-y: auto;
}

.detected-type, .alternative-types, .manual-classification {
  margin-bottom: 24px;
}

h3 {
  margin: 0 0 12px 0;
  font-size: 16px;
  color: #24292e;
}

.type-badge {
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  background-color: #f1f8ff;
  color: #0366d6;
  border-radius: 4px;
  font-weight: 500;
  margin-bottom: 12px;
}

.is-verified {
  background-color: #e6f7e6;
  color: #28a745;
}

.verified-icon {
  margin-left: 8px;
  font-weight: bold;
}

.verify-button {
  padding: 6px 12px;
  background-color: #0366d6;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.verify-button:hover {
  background-color: #0256b9;
}

.types-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.type-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background-color: #f6f8fa;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.type-item:hover {
  background-color: #f1f8ff;
}

.type-confidence {
  font-size: 14px;
  color: #6c757d;
}

.no-alternatives {
  color: #6c757d;
  font-style: italic;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
}

.form-group select {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #e1e4e8;
  border-radius: 4px;
  background-color: #f6f8fa;
  font-size: 14px;
}

.apply-button {
  padding: 6px 12px;
  background-color: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  width: 100%;
}

.apply-button:disabled {
  background-color: #e1e4e8;
  color: #6c757d;
  cursor: not-allowed;
}

.apply-button:not(:disabled):hover {
  background-color: #218838;
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  background-color: #ffffff;
  border-top: 1px solid #e1e4e8;
}

.primary-button, .secondary-button {
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}

.primary-button {
  background-color: #0366d6;
  color: white;
  border: none;
}

.primary-button:hover {
  background-color: #0256b9;
}

.secondary-button {
  background-color: #ffffff;
  color: #24292e;
  border: 1px solid #e1e4e8;
}

.secondary-button:hover {
  background-color: #f6f8fa;
}
</style>